import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetTruckCoverage } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * P8 — hur fulla lastbilarna in till och ut från Strängnäs var.
 *
 * De två riktningarna hålls isär med flit. En full inkommande och en tom utgående körning är
 * inte "halvfullt" — det är ett fullt inflöde och ett tomt utflöde, och de två är olika problem
 * att lösa.
 */
const TruckCoverageWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetTruckCoverage();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  const rows = Array.from(
    (result?.fillRates ?? [])
      .reduce((map, dp) => {
        const key = `${dp.direction}|${dp.registrationNumber}`;
        const current = map.get(key) ?? {
          direction: dp.direction,
          directionName: dp.directionName,
          truck: dp.registrationNumber,
          runs: 0,
          capacity: 0,
          loaded: 0,
        };
        current.runs += dp.runs;
        current.capacity += dp.capacity;
        current.loaded += dp.loadedCars;
        map.set(key, current);
        return map;
      }, new Map<string, { direction: number; directionName: string; truck: string; runs: number; capacity: number; loaded: number }>())
      .values()
  ).sort((a, b) => a.direction - b.direction || b.loaded - a.loaded);

  const missingCapacity = result
    ? result.totalRuns - result.runsWithConfiguredCapacity
    : 0;

  // Anläggningen saknas är inte samma sak som att tabellen är tom. Skulle vi låta den räknas
  // som "ingen data" tar WidgetState över och förklaringen nedan visas aldrig.
  const facilityMissing = result != null && !result.strangnasFacilityResolved;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.TruckCoverage]}
      count={Math.round(result?.totalFillRatePercentage ?? 0)}
      isLoading={getData.isLoading}
      info="Hur fulla lastbilarna som gick in till och ut från logistikanläggningen i Strängnäs var. Riktningen avgörs av körningens ändpunkter. Kapaciteten är antagen tills lastbilarnas konfiguration är rättad — en körning som lastade fler bilar än de antagna platserna räknas som full, så fyllnadsgraden stannar på 100 %."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!facilityMissing && rows.length === 0}
      >
        {facilityMissing ? (
          <p style={{ color: '#a33', margin: 0 }}>
            Logistikanläggningen i Strängnäs kunde inte hittas bland företagen. Fyllnadsgraden
            går inte att räkna fram — det betyder alltså inte att inga lastbilar kört.
          </p>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Riktning</th>
                  <th style={thStyle}>Lastbil</th>
                  <th style={thNumStyle}>Körningar</th>
                  <th style={thNumStyle}>Platser</th>
                  <th style={thNumStyle}>Lastade</th>
                  <th style={thNumStyle}>Fyllnadsgrad</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.direction}-${row.truck}`}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>{row.directionName}</td>
                    <td style={tdStyle}>{row.truck}</td>
                    <td style={tdNumStyle}>{nf(row.runs)}</td>
                    <td style={tdNumStyle}>{nf(row.capacity)}</td>
                    <td style={tdNumStyle}>{nf(row.loaded)}</td>
                    <td style={tdNumStyle}>
                      {row.capacity > 0 ? pct((row.loaded / row.capacity) * 100) : '–'}
                    </td>
                  </tr>
                ))}
                <tr style={totalRowStyle}>
                  <td style={tdStyle} colSpan={2}>
                    In till Strängnäs
                  </td>
                  <td style={tdNumStyle}>{nf(result?.inboundRuns ?? 0)}</td>
                  <td style={tdNumStyle}>{nf(result?.inboundCapacity ?? 0)}</td>
                  <td style={tdNumStyle}>{nf(result?.inboundLoadedCars ?? 0)}</td>
                  <td style={tdNumStyle}>{pct(result?.inboundFillRatePercentage)}</td>
                </tr>
                <tr style={{ fontWeight: 'bold' }}>
                  <td style={tdStyle} colSpan={2}>
                    Ut från Strängnäs
                  </td>
                  <td style={tdNumStyle}>{nf(result?.outboundRuns ?? 0)}</td>
                  <td style={tdNumStyle}>{nf(result?.outboundCapacity ?? 0)}</td>
                  <td style={tdNumStyle}>{nf(result?.outboundLoadedCars ?? 0)}</td>
                  <td style={tdNumStyle}>{pct(result?.outboundFillRatePercentage)}</td>
                </tr>
              </tbody>
            </table>

            <ControlFigures
              items={[
                result && missingCapacity > 0
                  ? `${nf(missingCapacity)} av ${nf(result.totalRuns)} körningar saknar satt kapacitet — fyllnadsgraden räknas på antagna ${nf(result.assumedCapacityPerRun)} platser per körning`
                  : null,
                result && result.runsOutsideHub > 0
                  ? `${nf(result.runsOutsideHub)} körningar varken började eller slutade i Strängnäs och ingår inte`
                  : null,
                result && result.runsExcludedByFilters > 0
                  ? `${nf(result.runsExcludedByFilters)} körningar föll bort på ärende- och leverantörsfiltret`
                  : null,
                result && result.runsWithoutDrivingEvent > 0
                  ? `${nf(result.runsWithoutDrivingEvent)} rörelser saknar avfärds- eller ankomstevent och dateras på uppdragets tider — de räknas med i fyllnadsgraden men kan ligga i fel vecka (en rundtur räknas som två rörelser)`
                  : null,
              ]}
            />
          </>
        )}
      </WidgetState>
    </Widget>
  );
};

export default TruckCoverageWidget;
